import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { listFactFindV2Versions } from '../../fact-find-test/api';
import Card from '../ui/Card';
import Badge from '../ui/Badge';
import { ClipboardListIcon, ExternalLinkIcon, ChevronDownIcon } from '../ui/icons';

// Merge of the old FactFindV2Card + FactFindLegacyCard into a single card so
// the application page stays tight: the v2 snapshots are the primary,
// internally-scrolling list, and the pre-migration v1 history is tucked into a
// collapsed "Legacy versions" disclosure. All print routes are unchanged.
const formatSnapshotDate = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
};

const snapshotReasonLabel = (reason) => {
    if (reason === 'submit') return 'Customer submit';
    if (reason === 'sweep') return 'Daily snapshot';
    return reason || '-';
};

const PrintLink = ({ href }) => (
    <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        title="Open print view"
        className="inline-flex items-center justify-center rounded-lg p-2 text-[#6B7280] hover:bg-[#EFEAFE] hover:text-[#5B3FE0] transition-colors"
    >
        <ExternalLinkIcon className="h-4 w-4" />
    </a>
);

const Row = ({ children }) => (
    <li className="flex items-center justify-between gap-3 px-5 sm:px-6 py-3.5">{children}</li>
);

const FactFindCard = () => {
    const application = useSelector((s) => s.admin.active_application.application);
    const form = useSelector((s) => s.admin.active_application.form);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [versions, setVersions] = useState([]);
    const [legacyOpen, setLegacyOpen] = useState(false);

    const applicationId = application && application.id ? application.id : null;

    useEffect(() => {
        const load = async () => {
            if (!applicationId) return;
            setLoading(true);
            setError('');
            try {
                const res = await listFactFindV2Versions(applicationId);
                if (res && res.ok) {
                    setVersions(res.versions || []);
                } else {
                    setError('Failed to load versions.');
                }
            } catch (err) {
                setError('Failed to load versions.');
            }
            setLoading(false);
        };
        load();
    }, [applicationId]);

    if (!applicationId) return null;

    // Legacy (v1) history — same guard and print routes as the old
    // FactFindLegacyCard, including the "application-" id prefixing fallbacks.
    const hasLegacyData = !!(form && form.data && Object.keys(form.data).length > 0);
    const legacyVersionKeys =
        form && form.versions && application && application.id ? Object.keys(form.versions) : [];
    const hasLegacy = hasLegacyData || legacyVersionKeys.length > 0;

    let legacyCurrentLink;
    if (application && application.id) {
        const id = application.id.startsWith('application-') ? application.id : `application-${application.id}`;
        legacyCurrentLink = `/adminform/${id}`;
    } else if (form && form.id) {
        const formId = form.id.startsWith('application-') ? form.id : `application-${form.id}`;
        legacyCurrentLink = `/adminform/${formId}`;
    } else {
        legacyCurrentLink = `/adminform/application-unknown`;
    }

    return (
        <Card
            title="Fact Find"
            subtitle="Snapshots of the fact find. Open any to view / save as PDF."
            icon={<ClipboardListIcon className="h-5 w-5" />}
            bodyClassName="p-0"
        >
            <div className="max-h-96 overflow-y-auto">
                <ul className="divide-y divide-[#F0F1F5]">
                    <Row>
                        <div>
                            <p className="text-sm font-bold text-[#1F2937]">Current</p>
                            <p className="text-xs text-[#6B7280]">Live form data</p>
                        </div>
                        <PrintLink href={`/admin/factfindform/${applicationId}/print`} />
                    </Row>

                    {loading ? (
                        <li className="px-5 sm:px-6 py-3.5 text-sm text-[#6B7280]">Loading versions...</li>
                    ) : null}
                    {error ? (
                        <li className="px-5 sm:px-6 py-3.5 text-sm text-[#DC2626]">{error}</li>
                    ) : null}

                    {(versions || []).map((v) => (
                        <Row key={v.version_id}>
                            <div className="min-w-0">
                                <p className="text-sm font-semibold text-[#1F2937] flex items-center gap-2 flex-wrap">
                                    {formatSnapshotDate(v.snapshot_at)}
                                    {v.completed ? <Badge tone="success">Submitted</Badge> : null}
                                </p>
                                <p className="text-xs text-[#6B7280]">{snapshotReasonLabel(v.snapshot_reason)}</p>
                            </div>
                            <PrintLink href={`/admin/factfindform/${applicationId}/versions/${v.version_id}/print`} />
                        </Row>
                    ))}
                </ul>
            </div>

            {hasLegacy ? (
                <div className="border-t border-[#F0F1F5]">
                    <button
                        type="button"
                        onClick={() => setLegacyOpen((o) => !o)}
                        className="w-full flex items-center justify-between gap-3 px-5 sm:px-6 py-3 text-left transition-colors hover:bg-[#FAFAFB]"
                        aria-expanded={legacyOpen}
                    >
                        <span className="flex items-center gap-2">
                            <span className="text-xs font-bold uppercase tracking-wide text-[#6B7280]">
                                Legacy versions
                            </span>
                            {legacyVersionKeys.length > 0 ? (
                                <Badge tone="neutral">{legacyVersionKeys.length}</Badge>
                            ) : null}
                        </span>
                        <ChevronDownIcon
                            className={`h-4 w-4 text-[#9CA0AE] transition-transform ${legacyOpen ? 'rotate-180' : ''}`}
                        />
                    </button>

                    {legacyOpen ? (
                        <div className="max-h-80 overflow-y-auto border-t border-[#F0F1F5]">
                            <ul className="divide-y divide-[#F0F1F5]">
                                <Row>
                                    <p className="text-sm font-bold text-[#1F2937]">Current</p>
                                    <PrintLink href={legacyCurrentLink} />
                                </Row>
                                {legacyVersionKeys.map((key) => (
                                    <Row key={key}>
                                        <p className="text-sm font-semibold text-[#1F2937]">{form.versions[key].date}</p>
                                        <PrintLink href={`/versions/${application.id}/${key}/${form.id}`} />
                                    </Row>
                                ))}
                            </ul>
                        </div>
                    ) : null}
                </div>
            ) : null}
        </Card>
    );
};

export default FactFindCard;
